
import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc, collection, query, where, getDocs, limit, writeBatch } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';
import type { AdminUser, Invite } from '../types';

export const useAuth = () => {
    const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
    const [authLoading, setAuthLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (user) {
                const userDocRef = doc(db, 'users', user.uid);
                const userDoc = await getDoc(userDocRef);
                if (userDoc.exists()) {
                    const userData = { id: user.uid, ...userDoc.data() } as AdminUser;
                    setCurrentUser(userData);
                    // Set dev mode in session storage for live editing features
                    if (userData.role === 'developer') {
                        sessionStorage.setItem('devMode', 'true');
                    } else {
                        sessionStorage.removeItem('devMode');
                    }
                } else {
                    console.warn("User exists in Auth but not in Firestore. They may need to re-login or were created via Google Sign-In without a doc yet.");
                    setCurrentUser(null);
                    sessionStorage.removeItem('devMode');
                }
            } else {
                setCurrentUser(null);
                sessionStorage.removeItem('devMode');
            }
            setAuthLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const login = useCallback(async (emailOrUsername: string, pass: string) => {
        let emailToLogin = emailOrUsername;

        // If it doesn't look like an email, assume it's a username
        if (!emailOrUsername.includes('@') && emailOrUsername.trim() !== '') {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where("username", "==", emailOrUsername.trim()), limit(1));
            
            try {
                const querySnapshot = await getDocs(q);
                if (querySnapshot.empty) {
                    // Throw an error that the login page can catch
                    throw new Error("auth/user-not-found");
                }
                const userDoc = querySnapshot.docs[0].data() as AdminUser;
                emailToLogin = userDoc.email;
            } catch(e) {
                throw new Error("auth/user-not-found");
            }
        }
        await auth.signInWithEmailAndPassword(emailToLogin, pass);
    }, []);

    const logout = useCallback(async () => {
        await auth.signOut();
    }, []);

    const loginWithGoogle = useCallback(async () => {
        const result = await auth.signInWithPopup(googleProvider);
        const user = result.user;

        if (!user) {
            throw new Error("Google sign-in failed, user not found.");
        }

        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
            // User already exists, log them in
            setCurrentUser({ id: user.uid, ...userDoc.data() } as AdminUser);
        } else {
            // New user, check for an invite
            const invitesRef = collection(db, 'invites');
            const q = query(invitesRef, where("email", "==", user.email), limit(1));
            const inviteSnapshot = await getDocs(q);

            if (inviteSnapshot.empty) {
                // No invite found, deny access
                await auth.signOut();
                throw new Error("Your email is not authorized for access.");
            } else {
                // Invite found, create user and delete invite
                const inviteDoc = inviteSnapshot.docs[0];
                const invite = { id: inviteDoc.id, ...inviteDoc.data() } as Invite;

                const newUser: Omit<AdminUser, 'id'> = {
                    email: user.email!,
                    role: invite.role,
                };
                
                const batch = writeBatch(db);
                batch.set(userDocRef, newUser);
                batch.delete(doc(db, 'invites', invite.id));
                await batch.commit();

                setCurrentUser({ id: user.uid, ...newUser });
            }
        }
    }, []);

    return { currentUser, authLoading, login, logout, loginWithGoogle };
};